import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  useTheme,
} from '@mui/material';
import type React from 'react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useSnackbar } from '../../contexts/SnackbarContext';
import { getTemplatePreviewContent } from '../../services/templateService';
import type { Template, TemplateType } from '../../types/template';
import TemplatePreviewDialog from '../Templates/TemplatePreviewDialog';

interface TemplateSelectorProps {
  selectedTemplates: Record<string, string>;
  onTemplateChange: (type: string, templateId: string) => void;
  availableTemplates: Template[];
  stacked?: boolean;
  type?: 'draft' | 'cv' | 'case_study';
  label?: string;
  title?: string;
}

const TemplateSelector: React.FC<TemplateSelectorProps> = ({
  selectedTemplates,
  onTemplateChange,
  availableTemplates,
  type = 'draft',
  label = 'Draft Response Template',
  title = 'Select Templates',
}) => {
  const theme = useTheme();
  const { showSnackbar } = useSnackbar();

  // Preview State
  const [showPreview, setShowPreview] = useState(false);
  const [previewFile, setPreviewFile] = useState<File | null>(null);
  const [previewFileName, setPreviewFileName] = useState<string | undefined>(undefined);
  const [previewType, setPreviewType] = useState<TemplateType>('draft');
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);

  const selectedTemplateId = selectedTemplates[type] || 'system_default';
  const selectedTemplate = availableTemplates.find((t) => t.id === selectedTemplateId);

  const handlePreview = async () => {
    if (!selectedTemplate) {
      return;
    }

    setIsPreviewLoading(true);
    try {
      const dataUrl = await getTemplatePreviewContent(selectedTemplate.storagePath);

      const arr = dataUrl.split(',');
      if (arr.length < 2) {
        throw new Error('Invalid data URL received from server');
      }

      const header = arr[0] || '';
      const data = arr[1] || '';

      const mimeMatch = header.match(/:(.*?);/);
      const mime = mimeMatch
        ? mimeMatch[1]
        : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

      const bstr = atob(data);
      let n = bstr.length;
      const u8arr = new Uint8Array(n);
      while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
      }
      const file = new File([u8arr], selectedTemplate.name, {
        type: mime || 'application/octet-stream',
      });

      setPreviewFile(file);
      setPreviewFileName(selectedTemplate.name);
      setPreviewType(selectedTemplate.type);
      setShowPreview(true);
    } catch (_error) {
      showSnackbar(
        'Failed to load preview. Please try downloading it from Manage Templates.',
        'error'
      );
    } finally {
      setIsPreviewLoading(false);
    }
  };

  return (
    <Box sx={{ mb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="subtitle1" fontWeight="bold">
          {title}
        </Typography>
        <Button
          component={Link}
          to="/templates"
          size="small"
          endIcon={<OpenInNewIcon />}
          sx={{ color: theme.palette.bidlogiq.brick }}
        >
          Manage Templates
        </Button>
      </Box>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 12 }}>
          <FormControl size="small" fullWidth>
            <InputLabel>{label}</InputLabel>
            <Select
              value={selectedTemplateId}
              label={label}
              onChange={(e) => onTemplateChange(type, e.target.value as string)}
            >
              <MenuItem value="system_default">System Default</MenuItem>
              {availableTemplates
                .filter((t) => t.type === type)
                .map((t) => (
                  <MenuItem key={t.id} value={t.id}>
                    {t.name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </Grid>
        {selectedTemplate && (
          <Grid size={{ xs: 12, md: 12 }}>
            <Button
              size="small"
              startIcon={
                isPreviewLoading ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <VisibilityIcon />
                )
              }
              onClick={handlePreview}
              disabled={isPreviewLoading}
              sx={{ color: theme.palette.primary.main, textTransform: 'none' }}
            >
              {isPreviewLoading ? 'Loading Preview...' : 'Preview Selected Template'}
            </Button>
          </Grid>
        )}
      </Grid>

      {/* Global Preview Dialog for existing templates */}
      {isPreviewLoading && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            bgcolor: 'rgba(0,0,0,0.3)',
            zIndex: 9999,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <CircularProgress color="primary" />
        </Box>
      )}
      <TemplatePreviewDialog
        open={showPreview}
        onClose={() => setShowPreview(false)}
        file={previewFile}
        fileName={previewFileName || ''}
        templateType={previewType}
      />
    </Box>
  );
};

export default TemplateSelector;
